import React, { useState, useEffect } from 'react';
import { Search, MoreVertical, Shield, CreditCard, Ban, Trash2, CheckCircle2, Crown, UserCog, UserMinus, UserCheck, Mail, History, Info, AlertTriangle, Blocks, SlidersHorizontal, MessageSquareOff, ImageOff, Lock, ZapOff, Link2Off, FolderX, Database, FolderSearch } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const MASTER_ADMIN_EMAIL = "arsgrouproblox@gmail.com";

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isLimitsModalOpen, setIsLimitsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  
  const [creditAmount, setCreditAmount] = useState(0);
  const [limitData, setLimitData] = useState({});

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error("[UserManagement] Sync Error:", err);
      toast.error("Database connection restricted. Check RLS policies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleUpdate = async (id, payload) => {
    try {
      const { error } = await supabase.from('profiles').update(payload).eq('id', id);
      if (error) throw error;
      fetchUsers();
      toast.success("Identity updated successfully.");
      return true;
    } catch (err) {
      toast.error("Update failed: " + err.message);
      return false;
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedUser) return;
    try {
      const { error } = await supabase.from('profiles').delete().eq('id', selectedUser.id);
      if (error) throw error;
      
      toast.success("Account node purged from repository.");
      setIsDeleteConfirmOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error("Purge failed.");
    }
  };

  const filteredUsers = (users || []).filter(u => 
    (u.full_name || "").toLowerCase().includes(search.toLowerCase()) || 
    (u.username || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-syne font-bold">Account Repository</h1>
          <p className="text-sonas-muted text-xs">Managing {users.length} live architect nodes.</p>
        </div>
        
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sonas-dim" />
          <Input 
            placeholder="Search by Identity or Username..." 
            className="pl-10 h-11 bg-sonas-surface2 border-sonas-border rounded-xl focus:ring-sonas-accent"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-sonas-surface border border-sonas-border rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-sonas-surface2/50 border-b border-sonas-border">
              <tr>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Architect Identity</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Access Level</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Token Reservoir</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Status</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-sonas-dim text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sonas-border">
              {loading ? (
                <tr>
                   <td colSpan="5" className="px-6 py-20 text-center text-sonas-dim text-xs uppercase font-bold animate-pulse">Scanning database...</td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                   <td colSpan="5" className="px-6 py-20 text-center text-sonas-dim text-xs uppercase font-bold">No nodes detected in current sector.</td>
                </tr>
              ) : filteredUsers.map((u) => (
                <tr key={u.id} className={cn("hover:bg-sonas-surface2/30 transition-colors group", u.is_suspended && "bg-red-500/5")}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border border-sonas-border">
                        <AvatarImage src={u.avatar_url} />
                        <AvatarFallback className="bg-sonas-surface2 text-sonas-accent font-bold">
                          {u.username?.charAt(0).toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-bold">{u.full_name || 'Anonymous'}</p>
                        <p className="text-[11px] text-sonas-dim">@{u.username || 'unidentified'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-2">
                      {u.is_admin && (
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 rounded-full border border-amber-500/20">
                          <Crown size={10} className="text-amber-500" />
                          <span className="text-[9px] font-bold text-amber-500 uppercase">Admin</span>
                        </div>
                      )}
                      {u.is_pro && (
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-500/10 rounded-full border border-blue-500/20">
                          <Shield size={10} className="text-blue-500" />
                          <span className="text-[9px] font-bold text-blue-500 uppercase">Pro</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={cn("text-sm font-mono font-bold", u.is_token_usage_frozen ? "text-amber-500" : "text-sonas-accent")}>
                      ${(u.balance || 0).toFixed(3)}
                      {u.is_token_usage_frozen && <span className="ml-2 text-[8px] uppercase tracking-tighter">(Frozen)</span>}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {u.is_suspended ? (
                      <span className="text-[10px] font-bold text-red-500 uppercase flex items-center gap-1">
                        <Ban size={10} /> Suspended
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold text-sonas-accent uppercase flex items-center gap-1">
                        <CheckCircle2 size={10} /> Operational
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-sonas-surface2">
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-sonas-surface border-sonas-border w-64 p-1.5 rounded-2xl shadow-2xl">
                        <DropdownMenuItem className="gap-2.5 cursor-pointer rounded-xl py-2.5" onClick={() => navigate(`/admin/users/${u.id}/projects`)}>
                          <FolderSearch size={15} className="text-amber-500" /> 
                          <span className="text-xs font-bold">Inspect Projects</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem className="gap-2.5 cursor-pointer rounded-xl py-2.5" onClick={() => {
                          setSelectedUser(u);
                          setIsDetailsModalOpen(true);
                        }}>
                          <Info size={15} className="text-blue-400" /> 
                          <span className="text-xs font-bold">Deep Inspection</span>
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem className="gap-2.5 cursor-pointer rounded-xl py-2.5" onClick={() => {
                          setSelectedUser(u);
                          setCreditAmount(0);
                          setIsCreditModalOpen(true);
                        }}>
                          <CreditCard size={15} className="text-sonas-accent" /> 
                          <span className="text-xs font-bold">Inject Tokens</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem className="gap-2.5 cursor-pointer rounded-xl py-2.5" onClick={() => {
                          setSelectedUser(u);
                          setLimitData({ 
                            project_limit: u.custom_project_limit || (u.is_pro ? 10 : 3), 
                            rate_limit: u.custom_rate_limit || (u.is_pro ? 5 : 2),
                            is_chat_restricted: u.is_chat_restricted,
                            is_image_upload_restricted: u.is_image_upload_restricted,
                            is_possessions_restricted: u.is_possessions_restricted,
                            is_project_restricted: u.is_project_restricted,
                            is_account_edit_restricted: u.is_account_edit_restricted,
                            is_token_usage_frozen: u.is_token_usage_frozen,
                            is_plugin_restricted: u.is_plugin_restricted
                          });
                          setIsLimitsModalOpen(true);
                        }}>
                          <SlidersHorizontal size={15} className="text-purple-400" /> 
                          <span className="text-xs font-bold">Restrict Capabilities</span>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator className="bg-sonas-border mx-2" />

                        <DropdownMenuItem 
                          className="gap-2.5 cursor-pointer rounded-xl py-2.5"
                          onClick={() => handleUpdate(u.id, { is_pro: !u.is_pro })}
                        >
                          <Blocks size={15} className="text-blue-500" /> 
                          <span className="text-xs font-bold">{u.is_pro ? 'Demote to Free' : 'Promote to Pro'}</span>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator className="bg-sonas-border mx-2" />
                        
                        <DropdownMenuItem 
                          className={cn(
                            "gap-2.5 cursor-pointer rounded-xl py-2.5 font-bold",
                            u.is_suspended ? "text-sonas-accent focus:bg-sonas-accent/10" : "text-amber-500 focus:bg-amber-500/10"
                          )}
                          onClick={() => handleUpdate(u.id, { is_suspended: !u.is_suspended })}
                        >
                          {u.is_suspended ? <UserCheck size={15} /> : <Ban size={15} />}
                          <span className="text-xs">{u.is_suspended ? 'Restore Access' : 'Suspend Architect'}</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem 
                          className="gap-2.5 cursor-pointer rounded-xl py-2.5 text-red-500 focus:bg-red-500/10 font-bold"
                          onClick={() => {
                            setSelectedUser(u);
                            setIsDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 size={15} />
                          <span className="text-xs">Purge Profile</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Capabilities Modal */}
      <Dialog open={isLimitsModalOpen} onOpenChange={setIsLimitsModalOpen}>
        <DialogContent className="glass-surface border-sonas-borderStrong max-w-md rounded-[32px] p-0 overflow-hidden">
          <div className="p-8 space-y-6">
            <div className="text-center space-y-1">
              <h3 className="font-syne text-xl font-bold">Node Restrictions</h3>
              <p className="text-xs text-sonas-muted">Granular controls for @{selectedUser?.username}</p>
            </div>
            
            <div className="space-y-4 max-h-[400px] overflow-y-auto sonas-scrollbar pr-2">
              <section className="space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim px-1">Resource Limits</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-sonas-muted uppercase ml-1">Projects</label>
                    <Input type="number" value={limitData.project_limit} onChange={e => setLimitData({...limitData, project_limit: e.target.value})} className="bg-sonas-surface2 h-10 rounded-xl text-center font-bold" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-sonas-muted uppercase ml-1">Rate (req/m)</label>
                    <Input type="number" value={limitData.rate_limit} onChange={e => setLimitData({...limitData, rate_limit: e.target.value})} className="bg-sonas-surface2 h-10 rounded-xl text-center font-bold" />
                  </div>
                </div>
              </section>

              <section className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim px-1">Capability Toggles</p>
                {[
                  { id: 'is_chat_restricted', label: 'Disable AI Chat', icon: MessageSquareOff },
                  { id: 'is_image_upload_restricted', label: 'Block Image Uploads', icon: ImageOff },
                  { id: 'is_possessions_restricted', label: 'Lock Vault Access', icon: Database },
                  { id: 'is_project_restricted', label: 'Restrict Projects', icon: FolderX },
                  { id: 'is_account_edit_restricted', label: 'Freeze Account Info', icon: Lock },
                  { id: 'is_token_usage_frozen', label: 'Freeze Tokens', icon: ZapOff },
                  { id: 'is_plugin_restricted', label: 'Restrict Studio Sync', icon: Link2Off },
                ].map(cap => (
                  <div key={cap.id} className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <cap.icon size={16} className="text-sonas-dim" />
                      <span className="text-xs font-medium">{cap.label}</span>
                    </div>
                    <Switch checked={limitData[cap.id]} onCheckedChange={v => setLimitData({...limitData, [cap.id]: v})} />
                  </div>
                ))}
              </section>
            </div>
          </div>

          <div className="p-4 bg-sonas-surface2 border-t border-white/5 flex gap-3">
            <Button variant="ghost" className="flex-1 rounded-xl" onClick={() => setIsLimitsModalOpen(false)}>Abort</Button>
            <Button 
              className="flex-1 rounded-xl bg-sonas-accent text-sonas-bg font-bold shadow-lg shadow-sonas-accent/20"
              onClick={() => handleUpdate(selectedUser.id, { 
                ...limitData,
                custom_project_limit: Number(limitData.project_limit), 
                custom_rate_limit: Number(limitData.rate_limit) 
              }).then(() => setIsLimitsModalOpen(false))}
            >
              Apply Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deep Inspection Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="glass-surface border-sonas-borderStrong max-w-lg rounded-3xl p-0 overflow-hidden">
          <div className="p-8 space-y-8">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2 border-sonas-accent">
                  <AvatarImage src={selectedUser?.avatar_url} />
                  <AvatarFallback className="bg-sonas-surface2 text-xl font-bold">
                    {selectedUser?.username?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-xl font-syne font-bold">{selectedUser?.full_name || 'Anonymous'}</h2>
                  <p className="text-xs text-sonas-dim font-mono">@{selectedUser?.username || 'unknown'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-sonas-dim uppercase tracking-widest">Architect Balance</p>
                <p className="text-2xl font-mono font-bold text-sonas-accent">${selectedUser?.balance?.toFixed(3)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-sonas-dim uppercase">Bio / Mission</p>
                <p className="text-xs text-sonas-muted leading-relaxed">{selectedUser?.bio || 'No bio provided.'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-sonas-dim uppercase">Capabilities</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-sonas-dim">Project Capacity</span>
                    <span className="font-bold">{selectedUser?.custom_project_limit || (selectedUser?.is_pro ? '10' : '3')}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-sonas-dim">Rate Limit</span>
                    <span className="font-bold">{selectedUser?.custom_rate_limit || (selectedUser?.is_pro ? '5/min' : '2/min')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-sonas-surface2 p-4 flex justify-end gap-3 border-t border-white/5">
             <Button variant="ghost" className="rounded-xl" onClick={() => setIsDetailsModalOpen(false)}>Close Dossier</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Token Injection Modal */}
      <Dialog open={isCreditModalOpen} onOpenChange={setIsCreditModalOpen}>
        <DialogContent className="glass-surface border-sonas-borderStrong max-w-sm rounded-3xl p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-sonas-accent/10 flex items-center justify-center mx-auto mb-4">
            <CreditCard className="h-6 w-6 text-sonas-accent" />
          </div>
          <DialogTitle className="font-syne text-xl">Inject Tokens</DialogTitle>
          <div className="py-6">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-sonas-accent">$</div>
              <Input 
                type="number" 
                className="bg-sonas-surface2 text-center h-14 text-2xl font-mono border-sonas-border rounded-2xl pl-8"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </div>
          </div>
          <Button 
            className="w-full h-12 bg-sonas-accent hover:bg-sonas-accent/90 text-sonas-bg font-bold rounded-2xl"
            onClick={async () => {
              const newBal = (selectedUser?.balance || 0) + Number(creditAmount);
              if (await handleUpdate(selectedUser.id, { balance: newBal })) {
                setIsCreditModalOpen(false);
              }
            }}
          >
            Commit Adjustment
          </Button>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent className="glass-surface max-w-sm rounded-[32px] p-8 border-none">
          <AlertDialogHeader className="space-y-4">
            <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <AlertDialogTitle className="text-2xl font-syne text-center">Purge Account?</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-sm text-sonas-muted leading-relaxed">
              This will permanently delete the profile of <span className="text-sonas-text font-bold">@{selectedUser?.username}</span> from the repository.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-col gap-3 mt-6">
            <AlertDialogAction onClick={handleDeleteAccount} className="w-full bg-red-500 hover:bg-red-600 font-bold rounded-2xl h-12">Purge Node</AlertDialogAction>
            <AlertDialogCancel className="w-full rounded-2xl h-12 bg-sonas-surface2/50 border-none text-sonas-text font-semibold">Abort</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;